import { useState } from 'react';
import { formatPrice } from '@holo/format';
import { Image, YStack, XStack, Text, CounterInput, Input } from '@holo/ui';
import { useCart } from '@/hooks';
import type { CartItem as CartItemProps } from '@holo/gql';

type Props = Omit<CartItemProps, 'createdAt' | 'updatedAt'>;

const CartItem = ({
  id,
  name,
  variantName,
  notes: initialNotes,
  image,
  quantity,
  unitTotal,
  unit,
  unitQuantity,
}: Props) => {
  const { removeItem, updateItem } = useCart();
  const [isVisible, setIsVisible] = useState(false);
  const [notes, setNotes] = useState(initialNotes || '');

  const toggleNotes = () => {
    setIsVisible(!isVisible);
  };

  const handleUpdateNotes = (value: string) => {
    setNotes(value);
    updateItem({ id, notes: value });
    setIsVisible(false);
  };

  const handleUpdateQuantity = (value: number) => {
    if (value) {
      updateItem({ id, quantity: value });
    } else {
      removeItem(id);
    }
  };

  return (
    <YStack mb="$3" pb="$3" borderBottomColor="$border" borderBottomWidth={1}>
      <XStack>
        <Image src={image} width={75} height={75} alt={name || ''} borderRadius={8} />

        <YStack ml="$3" flex={1} marginRight="$3">
          <Text fontWeight="$semi-bold" fontSize="$12" mb="$1">
            {name}
          </Text>

          {!!variantName && (
            <Text fontSize="$12" mb="$1">
              {variantName}
            </Text>
          )}

          <Text fontSize="$12">{`${formatPrice({ amount: unitTotal })} / ${
            unitQuantity && unitQuantity > 1 ? unitQuantity : ''
          } ${unit}`}</Text>

          <YStack mt="$3">
            {initialNotes && !isVisible && (
              <Text fontSize="$11" color="$darkGray">
                {initialNotes}
              </Text>
            )}

            {isVisible && <Input placeholder="Add notes" value={notes} onChangeText={setNotes} />}

            <XStack mt="$2">
              {isVisible ? (
                <XStack>
                  {initialNotes === notes ? (
                    <Text
                      fontSize="$12"
                      textDecorationLine="underline"
                      onPress={() => handleUpdateNotes('')}
                      cursor="pointer"
                      mr="$5"
                    >
                      Remove
                    </Text>
                  ) : (
                    <Text
                      fontSize="$12"
                      textDecorationLine="underline"
                      onPress={() => handleUpdateNotes(notes)}
                      cursor="pointer"
                      mr="$5"
                    >
                      Save
                    </Text>
                  )}

                  <Text fontSize="$12" textDecorationLine="underline" onPress={toggleNotes} cursor="pointer" mr="$5">
                    Cancel
                  </Text>
                </XStack>
              ) : (
                <XStack>
                  <Text fontSize="$12" textDecorationLine="underline" onPress={toggleNotes} cursor="pointer" mr="$5">
                    {notes ? 'Edit note' : 'Add note'}
                  </Text>
                </XStack>
              )}

              {!isVisible && (
                <Text fontSize="$12" textDecorationLine="underline" cursor="pointer" onPress={() => removeItem(id)}>
                  Remove
                </Text>
              )}
            </XStack>
          </YStack>
        </YStack>

        <YStack>
          <CounterInput value={quantity} onChange={handleUpdateQuantity} />
        </YStack>
      </XStack>
    </YStack>
  );
};

export default CartItem;
